import { useEffect, useRef, useState } from "react";
import ChatInput from "./ChatInput";
import ChatMessage from "./ChatMessage";
import SuggestedQuestions from "./SuggestedQuestions";
import {
   AI_ASSISTANT_API_URL,
   consumeStreamingResponse,
   extractAnswer,
   parseMaybeJson,
} from "./streamRagClient";
import {
   ASSISTANT_AVATAR,
   ASSISTANT_GREETING,
   ASSISTANT_NAME,
} from "./aiAssistantConfig";

const initialMessage = {
   id: "assistant-welcome",
   role: "assistant",
   text: ASSISTANT_GREETING,
};

function SparkleIcon() {
   return (
      <svg
         viewBox="0 0 64 64"
         xmlns="http://www.w3.org/2000/svg"
         aria-hidden="true"
      >
         <path
            d="M26 6l5.8 13.2L45 25l-13.2 5.8L26 44l-5.8-13.2L7 25l13.2-5.8L26 6z"
            fill="currentColor"
         />
         <path
            d="M49 9l2.4 5.6L57 17l-5.6 2.4L49 25l-2.4-5.6L41 17l5.6-2.4L49 9z"
            fill="currentColor"
         />
         <path
            d="M52 31l2.8 6.4L61 40l-6.2 2.6L52 49l-2.8-6.4L43 40l6.2-2.6L52 31z"
            fill="currentColor"
         />
      </svg>
   );
}

function IconExternalLink() {
   return (
      <svg
         viewBox="0 0 24 24"
         width="14"
         height="14"
         fill="none"
         stroke="currentColor"
         strokeWidth="2"
         strokeLinecap="round"
         strokeLinejoin="round"
         aria-hidden="true"
      >
         <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
         <path d="M15 3h6v6" />
         <path d="M10 14 21 3" />
      </svg>
   );
}

function AiAssistantSection() {
   const [messages, setMessages] = useState([initialMessage]);
   const [isLoading, setIsLoading] = useState(false);
   const scrollRef = useRef(null);

   useEffect(() => {
      if (scrollRef.current) {
         scrollRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
   }, [messages, isLoading]);

   const updateAssistantMessage = (assistantId, patch) => {
      setMessages((prev) =>
         prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m))
      );
   };

   const sendQuestion = async (question) => {
      const trimmed = question.trim();
      if (!trimmed || isLoading) return;

      const assistantId = `assistant-${Date.now()}`;

      setMessages((prev) => [
         ...prev,
         {
            id: `user-${Date.now()}`,
            role: "user",
            text: trimmed,
         },
         {
            id: assistantId,
            role: "assistant",
            text: "",
            isStreaming: true,
         },
      ]);

      setIsLoading(true);

      try {
         const response = await fetch(AI_ASSISTANT_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               question: trimmed,
               assistantType: "portfolio",
               stream: true,
            }),
         });

         if (!response.ok) {
            const rawBody = await response.text();
            const parsed = parseMaybeJson(rawBody);
            throw new Error(
               extractAnswer(parsed) ||
                  `Request failed with status ${response.status}`
            );
         }

         let accumulated = "";

         await consumeStreamingResponse(response, (event) => {
            if (event.type === "status") {
               updateAssistantMessage(assistantId, {
                  streamStatus: event.status,
               });
               return;
            }

            if (event.type !== "text" || !event.text) return;

            accumulated += event.text;
            updateAssistantMessage(assistantId, {
               text: accumulated,
               streamStatus: undefined,
            });
         });

         if (!accumulated.trim()) {
            updateAssistantMessage(assistantId, {
               text: "I could not generate a response right now.",
            });
         }
      } catch (error) {
         const fallbackError =
            "Sorry, I am unable to respond right now. Please try again in a moment.";
         const normalizedError = String(error?.message || "").trim();
         const isNetworkError =
            normalizedError.toLowerCase().includes("failed to fetch") ||
            normalizedError.toLowerCase().includes("networkerror");

         updateAssistantMessage(assistantId, {
            text: isNetworkError
               ? "I could not reach the AI server. Please check API URL/CORS and try again."
               : normalizedError || fallbackError,
            isError: true,
            isStreaming: false,
         });
      } finally {
         updateAssistantMessage(assistantId, {
            isStreaming: false,
            streamStatus: undefined,
         });
         setIsLoading(false);
      }
   };

   return (
      <section id="ai-assistant-section">
         <div className="ai-assistant-card">
            <div className="ai-assistant-header-row">
               <div className="ai-assistant-title-group">
                  <span className="ai-assistant-sparkles" aria-hidden="true">
                     <span className="ai-assistant-sparkle">
                        <SparkleIcon />
                     </span>
                  </span>
                  <h2 className="ai-assistant-title">AI Assistant</h2>
                  <span className="ai-assistant-status">
                     <span className="ai-assistant-status-dot" />
                  </span>
               </div>
               <a
                  href="/ai-assistant"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ai-assistant-extended-link"
                  aria-label="View extended version, opens in a new tab"
               >
                  <span className="ai-assistant-extended-link__inner">
                     <span className="ai-assistant-extended-link__icon" aria-hidden="true">
                        <IconExternalLink />
                     </span>
                     <span className="ai-assistant-extended-link__text">
                        View extended version
                     </span>
                  </span>
               </a>
            </div>

            <div className="ai-assistant-welcome-row">
               <div className="ai-assistant-welcome-avatar-wrap">
                  <img
                     className="ai-assistant-welcome-avatar"
                     src={ASSISTANT_AVATAR}
                     alt={`${ASSISTANT_NAME} avatar`}
                  />
               </div>
               <div className="ai-assistant-welcome-bubble">
                  <p>
                     Hi! I'm{" "}
                     <span className="ai-assistant-welcome-accent">
                        {ASSISTANT_NAME}
                     </span>
                     . How can I help you today?
                  </p>
               </div>
            </div>

            <SuggestedQuestions
               onSelectQuestion={sendQuestion}
               disabled={isLoading}
            />

            <div className="chat-messages-area">
               {messages.slice(1).map((message) => (
                  <div
                     key={message.id}
                     className={`chat-message-row ${
                        message.role === "assistant"
                           ? "chat-message-row-assistant"
                           : "chat-message-row-user"
                     }`}
                  >
                     {message.role === "assistant" && (
                        <img
                           className="chat-message-avatar"
                           src={ASSISTANT_AVATAR}
                           alt={`${ASSISTANT_NAME} avatar`}
                        />
                     )}
                     <ChatMessage
                        role={message.role}
                        text={message.text}
                        isError={message.isError}
                        isStreaming={message.isStreaming}
                        streamStatus={message.streamStatus}
                     />
                  </div>
               ))}

               <div ref={scrollRef} />
            </div>

            <ChatInput onSend={sendQuestion} disabled={isLoading} />

            <div className="ai-assistant-footnote">
               <span className="ai-assistant-footnote-icon" aria-hidden="true">
                  🔒
               </span>
               <span>AI responses are based on my portfolio data.</span>
            </div>
         </div>
      </section>
   );
}

export default AiAssistantSection;
